import { router } from 'expo-router';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import { isNonMobileWeb, ScreenContainer, View, YStack } from 'smoothie';

import type { User } from '../../../../user/types/User';
import { AccountOwnership } from '../../../types/BankAccount';
import AccountOpenSuccess from './steps/AccountOpenSuccess';
import ConfirmAccountOpen from './steps/ConfirmAccountOpen';
import SelectOwnership from './steps/SelectOwnership';

enum SpaceOwnershipStep {
    SELECT_OWNERSHIP = 'selectOwnership',
    CONFIRM_ACCOUNT_OPEN = 'confirmAccountOpen',
    ACCOUNT_OPEN_SUCCESS = 'accountOpenSuccess',
}

export const enum AccountCreatedStatus {
    NotCreated = 'notCreated',
    JointCreated = 'jointCreated',
    IndividualCreated = 'individualCreated',
}

interface SpaceOwnershipProps {
    user: User;
    fromKyc: boolean;
    accountCreated?: AccountCreatedStatus;
    onInfoButtonPress: () => void;
    onSubmitCreateAccount: (jointEmail?: string) => Promise<void>;
    isJointInvitation: boolean;
    onAcceptJointInvitation: () => Promise<void>;
    onContinueFromAccountOpenSuccess: () => void;
    onContinueFromAccountOpenSuccessJoint: () => void;
}

const SpaceOwnershipTabs = ({
    user,
    fromKyc,
    accountCreated,
    onInfoButtonPress,
    onSubmitCreateAccount,
    isJointInvitation,
    onAcceptJointInvitation,
    onContinueFromAccountOpenSuccess,
    onContinueFromAccountOpenSuccessJoint,
}: SpaceOwnershipProps) => {
    const routes = Object.values(SpaceOwnershipStep).map((step) => ({
        key: step,
    }));

    const getRouteIndex = (step: SpaceOwnershipStep) =>
        routes.findIndex(({ key }) => key === step);

    const accountOpenSuccessIndex = getRouteIndex(
        SpaceOwnershipStep.ACCOUNT_OPEN_SUCCESS,
    );
    const confirmAccountOpenIndex = getRouteIndex(
        SpaceOwnershipStep.CONFIRM_ACCOUNT_OPEN,
    );
    const selectOwnershipIndex = getRouteIndex(
        SpaceOwnershipStep.SELECT_OWNERSHIP,
    );

    const getStartingIndex = () => {
        if (
            accountCreated === AccountCreatedStatus.IndividualCreated ||
            accountCreated === AccountCreatedStatus.JointCreated
        ) {
            return accountOpenSuccessIndex;
        } else if (isJointInvitation) {
            return confirmAccountOpenIndex;
        } else {
            return selectOwnershipIndex;
        }
    };

    const [index, setIndex] = useState(getStartingIndex());
    const [jointEmailAddress, setJointEmailAddress] = useState<
        string | undefined
    >(undefined);
    const [tabHistoryStack, setTabHistoryStack] = useState<number[]>([
        getStartingIndex(),
    ]);

    const handleSetNextPage = (nextPage: number) => {
        setIndex(nextPage);
        setTabHistoryStack((prevState) => {
            return [...prevState, nextPage];
        });
    };

    const onPressBack = () => {
        if (tabHistoryStack.length > 1) {
            setIndex(tabHistoryStack[tabHistoryStack.length - 2] ?? 0);
            setTabHistoryStack((prevState) => {
                return prevState.filter((i) => i !== index);
            });
        } else {
            // Don't go back to joint invite page (it's just a landing page with a forever loader)
            if (!isJointInvitation && router.canGoBack()) {
                router.back();
            } else {
                router.replace('/home/banking');
            }
        }
    };

    const showBackButton = index !== accountOpenSuccessIndex;

    useEffect(() => {
        if (
            accountCreated === AccountCreatedStatus.JointCreated ||
            accountCreated === AccountCreatedStatus.IndividualCreated
        ) {
            handleSetNextPage(accountOpenSuccessIndex);
        }
    }, [accountCreated]);

    const handleCreateAccount = async () => {
        await onSubmitCreateAccount(jointEmailAddress);
    };

    const onOwnershipSelect = (ownership: AccountOwnership) => {
        if (ownership === AccountOwnership.INDIVIDUAL) {
            setJointEmailAddress(undefined);
        } else if (
            ownership === AccountOwnership.JOINT &&
            user.partnerMember?.email != null
        ) {
            setJointEmailAddress(user.partnerMember.email);
        }

        handleSetNextPage(confirmAccountOpenIndex);
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: SpaceOwnershipStep } }) => {
        if (routes[index]?.key !== route.key) {
            return null;
        }

        const getStepContent = () => {
            switch (route.key) {
                case SpaceOwnershipStep.SELECT_OWNERSHIP:
                    return (
                        <SelectOwnership
                            onPressNext={onOwnershipSelect}
                            partnerMember={user.partnerMember}
                            onInfoButtonPress={onInfoButtonPress}
                        />
                    );
                case SpaceOwnershipStep.CONFIRM_ACCOUNT_OPEN:
                    return (
                        <ConfirmAccountOpen
                            jointEmail={jointEmailAddress}
                            showDisclosures={!fromKyc}
                            isJointInvitation={isJointInvitation}
                            onSubmit={
                                isJointInvitation
                                    ? onAcceptJointInvitation
                                    : handleCreateAccount
                            }
                        />
                    );
                case SpaceOwnershipStep.ACCOUNT_OPEN_SUCCESS:
                    return (
                        <YStack paddingTop={isNonMobileWeb ? 140 : 80}>
                            <AccountOpenSuccess
                                isJointInvitation={isJointInvitation}
                                onPressNext={
                                    isJointInvitation
                                        ? onContinueFromAccountOpenSuccessJoint
                                        : onContinueFromAccountOpenSuccess
                                }
                            />
                        </YStack>
                    );
                default:
                    return null;
            }
        };

        return (
            <ScreenContainer
                hasBackButton={showBackButton}
                onBack={onPressBack}
                onFallbackRoute="/home/banking"
            >
                {getStepContent()}
            </ScreenContainer>
        );
    };

    return (
        <View height="100%">
            <TabView
                swipeEnabled={false}
                navigationState={{ index, routes }}
                renderTabBar={() => <></>}
                renderScene={renderScene}
                onIndexChange={setIndex}
            />
        </View>
    );
};

export default SpaceOwnershipTabs;
